import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Clock, Users, ArrowRight } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredPackages, setFilteredPackages] = useState([]);
  const { formatPrice } = useCurrency();

  const categories = [
    { value: 'all', label: 'All Safaris' },
    { value: 'day', label: 'Day Safaris' },
    { value: 'weekend', label: 'Weekend Packages' },
    { value: 'extended', label: 'Extended Tours' },
  ];

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        setPackages(response.data);
        setFilteredPackages(response.data);
      } catch (error) {
        console.error('Error fetching packages:', error);
      }
    };
    fetchPackages();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredPackages(packages);
    } else {
      setFilteredPackages(
        packages.filter((pkg) => pkg.category.toLowerCase() === selectedCategory)
      );
    }
  }, [selectedCategory, packages]);

  return (
    <div className="bg-sand min-h-screen pt-20" data-testid="packages-page">
      {/* Header */}
      <section className="py-24 px-4 bg-navy">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="font-heading text-6xl md:text-8xl font-light tracking-tight leading-[0.9] text-gold mb-6">
            Safari Packages
          </h1>
          <p className="font-body text-base md:text-lg text-sand/80 leading-relaxed max-w-2xl mx-auto">
            Choose from our carefully crafted wildlife experiences
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-20 px-4 border-b border-border" data-testid="category-filters">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                data-testid={`filter-${category.value}`}
                className={`px-6 py-3 rounded-full font-body font-medium transition-all ${
                  selectedCategory === category.value
                    ? 'bg-navy text-gold'
                    : 'bg-white text-navy hover:bg-muted'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          {filteredPackages.length === 0 ? (
            <div className="text-center py-16" data-testid="no-packages">
              <p className="font-body text-lg text-muted-foreground">
                No packages found in this category.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="packages-grid">
              {filteredPackages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`package-card-${index}`}
                >
                  <Link to={`/packages/${pkg.id}`}>
                    <div className="group relative overflow-hidden rounded-xl bg-white shadow-xl transition-all hover:-translate-y-1 hover:shadow-2xl">
                      <div className="aspect-[4/3] overflow-hidden relative">
                        <img
                          src={pkg.image_url}
                          alt={pkg.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute top-4 right-4 bg-emerald-600 text-white px-4 py-2 rounded-full price-luxe-bold text-base shadow-md flex flex-col items-center leading-tight">
                          <span>{formatPrice(pkg.price)}</span>
                          {pkg.pricing_type === 'per_person' && (
                            <span className="text-[10px] font-body font-medium uppercase tracking-wide opacity-90">/ person</span>
                          )}
                        </div>
                      </div>
                      <div className="p-6">
                        <span className="font-accent text-xs uppercase tracking-widest text-muted-foreground">
                          {pkg.category}
                        </span>
                        <h3 className="font-heading text-2xl font-normal text-navy my-3">
                          {pkg.name}
                        </h3>
                        <p className="font-body text-sm text-foreground/70 mb-4 line-clamp-2">
                          {pkg.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-1">
                              <Clock size={16} />
                              <span>{pkg.duration}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Users size={16} />
                              <span>{pkg.max_guests}</span>
                            </div>
                          </div>
                          <ArrowRight
                            size={20}
                            className="text-emerald-600 group-hover:translate-x-1 transition-transform"
                          />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Packages;